import { Button, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ibsys/kit';
import { AlertTriangleIcon, CopyIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toastSuccess } from '@/shared/lib/utils/toast';

type Props = {
  token: string;
  onClose: () => void;
};

export const TokenCreatedDialog: FC<Props> = ({ token, onClose }) => {
  const { t } = useTranslation('users');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      toastSuccess(t('tokenCopied'));
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = token;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toastSuccess(t('tokenCopied'));
    }
  };

  return (
    <DialogContent
      className="max-w-lg"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>{t('tokenCreated')}</DialogTitle>
      </DialogHeader>

      <DialogDescription className="flex items-start gap-2 text-warning-foreground">
        <AlertTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
        <span>{t('tokenCreatedWarning')}</span>
      </DialogDescription>

      <div className="flex gap-2 items-start">
        <div className="flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all select-all">{token}</div>
        <Button variant="outline" size="sm" icon={<CopyIcon className="w-4 h-4" />} onClick={handleCopy}>
          {t('copyToken')}
        </Button>
      </div>

      <DialogFooter>
        <Button onClick={onClose}>{t('done')}</Button>
      </DialogFooter>
    </DialogContent>
  );
};
